import { useEffect, useState } from 'react';
import type { AuditEvent } from '@asko/shared';
import { ApiError, api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDateTime, formatTime } from '../lib/format';

/**
 * Hodisalar tasmasi.
 *
 * Savol bitta: «shu yerda nima bo'ldi?». Ilgari javob faqat server
 * loglarida edi va uni har safar biz orqali olish kerak bo'lardi.
 *
 * Tafsilotlar yopiq turadi: kundalik ishda sana, amal va kim
 * bajargani yetadi; ichkarisi esa nosozlik qidirilganda ochiladi.
 */

interface EventTimelineProps {
  /** Qaysi manzildan olinadi. */
  source: string;
  emptyText: string;
}

/** Yukdagi provayder nomi, bo'lsa. */
function providerOf(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') return null;
  const provider = (payload as { provider?: unknown }).provider;
  return typeof provider === 'string' ? provider : null;
}

export function EventTimeline({ source, emptyText }: EventTimelineProps) {
  const { t, apiMessage, eventLabel } = useI18n();
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setError(null);

    api
      .get<AuditEvent[]>(source)
      .then((list) => {
        if (!cancelled) setEvents(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err : null);
      });

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return (
      <div className="alert alert--error" role="alert">
        {apiMessage(error)}
      </div>
    );
  }

  if (!events) {
    return <div className="spinner" aria-label={t('common.loading')} />;
  }

  if (events.length === 0) {
    return <p className="section-hint">{emptyText}</p>;
  }

  return (
    <ol className="timeline">
      {events.map((event) => {
        const key = `${event.entity}.${event.action}`;
        const isOpen = opened === event.id;

        return (
          <li key={event.id} className="timeline__item">
            <span className="timeline__time">
              {formatDateTime(event.at)} {formatTime(event.at)}
            </span>

            <span className="timeline__what">
              {/* Tanish amallar odam tilida, notanishi — o'z nomi bilan:
                  yangi hodisa qo'shilganda tasma jim qolib ketmasin. */}
              {eventLabel(key)}
              {/* Provayder nomi darrov ko'rinsin: aks holda ikkita
                  «Provayder tasdiqladi» satrini ajratib bo'lmaydi. */}
              {providerOf(event.payload) && (
                <span className="timeline__tag">{providerOf(event.payload)}</span>
              )}
            </span>

            <span className="timeline__who">{event.actorName ?? '—'}</span>

            {/*
              Tugma bo'lmasa ham katak qoladi: aks holda ustunlar
              qatordan qatorga sakraydi va tasma tartibsiz ko'rinadi.
            */}
            <span className="timeline__action">
              {event.payload != null && (
                <button
                  className="timeline__more"
                  type="button"
                  onClick={() => setOpened(isOpen ? null : event.id)}
                >
                  {isOpen ? t('event.hide') : t('event.details')}
                </button>
              )}
            </span>

            {isOpen && (
              <pre className="timeline__payload">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            )}
          </li>
        );
      })}
    </ol>
  );
}
